import { useEffect, useSyncExternalStore } from 'react';
import type { Api } from '@limuzin/kit';
import { getDaylight } from './api';

/**
 * Whether it is light outside the town the passenger is waiting in — a port of the
 * mobile rider app's `daylightProvider`.
 *
 * The ride is headed by a picture of a street painted more than once, and this says
 * whether the one to draw is the night street. It is not the theme: «Тема» is a
 * preference, and at eleven at night the car is coming down a dark street whichever
 * one was chosen. The theme picks only between the two daylight paintings.
 *
 * The server decides, because sunrise is a fact about where the town is (ADR 0016),
 * and says until when the answer holds; so this asks once, and again at each sunrise
 * and sunset for as long as the tab is open — twice a day, not a poll. Until an
 * answer arrives, and if none ever does, it is daylight: a street in the wrong light
 * is a smaller wrong than no street at all.
 */
let night = false;
let following: string | null = null;
let timer = 0;
const listeners = new Set<() => void>();

/** The least this waits before asking again, whatever it was told: a server whose
 * clock is behind this one's would otherwise hand back an instant already past. */
const LEAST = 60_000;

function ask(api: Api, townId: string): void {
  window.clearTimeout(timer);
  void getDaylight(api, townId)
    .then((answer) => {
      if (following !== townId) return;
      const dark = answer.phase === 'NIGHT';
      if (dark !== night) {
        night = dark;
        listeners.forEach((heard) => heard());
      }
      again(api, townId, Date.parse(answer.until) - Date.now());
    })
    // Not worth telling the passenger about: the street is the one it was. Again
    // within the hour.
    .catch(() => again(api, townId, 60 * 60_000));
}

function again(api: Api, townId: string, wait: number): void {
  if (following !== townId) return;
  window.clearTimeout(timer);
  timer = window.setTimeout(() => ask(api, townId), Math.max(LEAST, wait));
}

/** Follows the town the passenger is in; a new town moves sunrise with it. */
export function useFollowDaylight(api: Api, townId: string | null): void {
  useEffect(() => {
    if (!townId) return;
    following = townId;
    ask(api, townId);
    return () => {
      if (following === townId) following = null;
      window.clearTimeout(timer);
    };
  }, [api, townId]);
}

export function useNight(): boolean {
  return useSyncExternalStore(
    (heard) => {
      listeners.add(heard);
      return () => listeners.delete(heard);
    },
    () => night,
  );
}

/**
 * The file for one picture of a ride. The sky is asked first — a dark street is a
 * dark street in either theme — and [dark] chooses only between the two daylight
 * ones.
 */
export function ridePicture(name: string, night: boolean, dark: boolean): string {
  return `/rides/${name}-${night ? 'night' : dark ? 'dark' : 'day'}.webp`;
}

/**
 * Fetched before there is a ride to head: the moment each picture heads is one the
 * passenger is already looking at the screen for, and one that arrived a beat after
 * «Заказ принят» would flash grey over it.
 */
export function usePreloadPictures(night: boolean, dark: boolean): void {
  useEffect(() => {
    for (const name of ['podacha', 'na_meste', 'v_puti', 'stop']) {
      const image = new Image();
      image.src = ridePicture(name, night, dark);
    }
  }, [night, dark]);
}
